import { Layout } from "@/components/layout/Layout";
import { usePageMeta } from "@/hooks/use-page-meta";
import { site } from "@/data/site";
import { Link } from "wouter";

const LAST_UPDATED = "July 16, 2026";

export default function DataDeletion() {
  usePageMeta(
    "Data Deletion Instructions",
    "How to request deletion of any personal data held by travisjohnson.xyz."
  );

  return (
    <Layout>
      <div className="container mx-auto px-6 max-w-3xl py-12 md:py-20">
        <h1 className="text-4xl md:text-5xl font-serif font-medium tracking-tight mb-4">
          Data Deletion Instructions
        </h1>
        <p className="text-sm text-muted-foreground mb-12">
          Last updated: {LAST_UPDATED}
        </p>

        <div className="prose prose-lg dark:prose-invert max-w-none space-y-10">

          <section>
            <h2 className="text-2xl font-serif font-medium text-foreground mb-4 mt-0">What Data This Site Holds</h2>
            <p className="text-muted-foreground leading-relaxed">
              <strong className="text-foreground">travisjohnson.xyz</strong> is a personal portfolio website
              operated by Travis Johnson. The Site collects and stores minimal personal data:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2 mt-3">
              <li>
                <strong className="text-foreground">Contact form submissions</strong> — if you used the
                contact form, your name, email address, subject, message, and optional company name were
                sent to me directly. This is the only personally identifiable information stored.
              </li>
              <li>
                <strong className="text-foreground">Server access logs</strong> — standard web server logs
                that may include your IP address and browser user-agent. These are automatically purged on
                a rolling 30-day basis.
              </li>
            </ul>
            <p className="text-muted-foreground leading-relaxed mt-4">
              This Site does not use Facebook Login, does not create Facebook-linked user accounts, and
              does not store any Facebook or Instagram user data. The Meta Ad Library API is used in
              read-only mode, server-side, to retrieve publicly available advertising data for private
              research — no Facebook user data is collected or retained through this integration.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-serif font-medium text-foreground mb-4">How to Request Data Deletion</h2>
            <p className="text-muted-foreground leading-relaxed">
              To request deletion of any personal data I hold about you, please contact me directly:
            </p>

            <div className="mt-4 p-5 bg-muted rounded-xl text-sm space-y-3">
              <div>
                <p className="font-medium text-foreground mb-1">Option 1 — Email</p>
                <p className="text-muted-foreground">
                  Send an email to{" "}
                  <a href={`mailto:${site.email}?subject=Data Deletion Request`}
                    className="text-foreground font-medium hover:underline">
                    {site.email}
                  </a>{" "}
                  with the subject line <em>"Data Deletion Request"</em> and include:
                </p>
                <ul className="list-disc pl-5 mt-2 text-muted-foreground space-y-1">
                  <li>The name and email address used when contacting the site;</li>
                  <li>A brief description of what data you'd like removed.</li>
                </ul>
              </div>
              <div className="border-t border-border pt-3">
                <p className="font-medium text-foreground mb-1">Option 2 — Contact Form</p>
                <p className="text-muted-foreground">
                  Use the{" "}
                  <Link href="/contact" className="text-foreground font-medium hover:underline">
                    contact form
                  </Link>{" "}
                  on this site with the subject "Data Deletion Request."
                </p>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-serif font-medium text-foreground mb-4">What Happens After You Request</h2>
            <ol className="list-decimal pl-6 text-muted-foreground space-y-2">
              <li>I will acknowledge your request within <strong className="text-foreground">5 business days</strong>.</li>
              <li>Any contact form data I hold linked to your email address will be permanently deleted within <strong className="text-foreground">30 days</strong> of the confirmed request.</li>
              <li>I will confirm deletion in writing by replying to your request.</li>
            </ol>
            <p className="text-muted-foreground leading-relaxed mt-4">
              Note: server access logs that may contain your IP address are purged automatically on a
              rolling 30-day basis regardless of deletion requests. There is no way to expedite this, nor
              can specific IP addresses be targeted for early removal from aggregated logs.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-serif font-medium text-foreground mb-4">Contact</h2>
            <div className="p-4 bg-muted rounded-lg text-muted-foreground text-sm space-y-1">
              <p><strong className="text-foreground">Travis Johnson</strong></p>
              <p>{site.location}</p>
              <p>
                Email:{" "}
                <a href={`mailto:${site.email}`} className="text-foreground hover:underline">
                  {site.email}
                </a>
              </p>
            </div>
          </section>

          <p className="text-sm pt-8 border-t border-border text-muted-foreground">
            Last updated: {LAST_UPDATED} ·{" "}
            <Link href="/privacy" className="text-foreground hover:underline">Privacy Policy</Link>
            {" · "}
            <Link href="/terms" className="text-foreground hover:underline">Terms of Use</Link>
          </p>
        </div>
      </div>
    </Layout>
  );
}
